import type { MeasurementReviewField } from "./measurement-review";
import { subjectMeasurementAxis } from "./measurement-subject-fields";

const normalise = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

export function isFootingLength(field: MeasurementReviewField) {
  return field.unit === "m" &&
    /\b(?:footings?|foundations?|trenches|trench)\b/i.test(field.label) &&
    /\b(?:length|run|perimeter)\b/i.test(field.label);
}

export function measurementDisplay(field: MeasurementReviewField) {
  const millimetres = field.unit === "m" && !isFootingLength(field);
  return {
    unit: millimetres ? "mm" : field.unit,
    value: millimetres && field.value.trim() !== ""
      ? String(Number((Number(field.value) * 1000).toFixed(6))) : field.value,
    millimetres,
  };
}

function extensionAxis(field: MeasurementReviewField) {
  if (field.removed || (field.subjectKind && field.subjectKind !== "extension")) return null;
  const label = normalise(field.label);
  if (/\b(?:walls?|openings?|doors?|windows?|foundations?|footings?|trenches|trench|excavations?|bearings?|returns?|runs?|beams?)\b/.test(label)) return null;
  if (field.subjectKind !== "extension" && !/\bextension\b/.test(label)) return null;
  const planAxis = subjectMeasurementAxis("extension", field.label);
  const axis = field.unit === "m²" && /\b(?:area|footprint)\b/.test(label) ? "area" :
    field.unit === "m" && (planAxis === "length" || planAxis === "width") ? planAxis : null;
  if (!axis) return null;
  const scopeName = normalise((field.subjectLabel || label)
    .replace(/\b(?:length|depth|projection|width|frontage|breadth|footprint|floor|area|overall|new|proposed|total)\b/gi, ""));
  const scope = field.subjectId || scopeName;
  return { axis, scope, scopeName, subjectId: field.subjectId,
    label: field.subjectLabel || field.label.replace(/\(?\b(?:length|depth|projection|width|frontage|breadth|footprint area|area)\)?\s*$/i, "").trim() };
}

type ExtensionAxis = NonNullable<ReturnType<typeof extensionAxis>>;
function belongsToScope(area: ExtensionAxis, dimension: ExtensionAxis, groups: ExtensionAxis[]) {
  if (area.subjectId && dimension.subjectId) return area.subjectId === dimension.subjectId;
  if (area.scopeName === dimension.scopeName &&
    groups.filter(g => g.scopeName === area.scopeName).length === 1) return true;
  return groups.length === 1 && area.scopeName === "extension" && !area.subjectId;
}

/** Hide a second AI-read extension footprint once the reviewed axes have
 * produced a calculated footprint. Keep the raw drawing evidence untouched.
 * Never suppress a builder edit or choose between multiple extension areas.
 */
export function withoutDuplicateCalculatedFootprint(fields: MeasurementReviewField[]) {
  const calculated = fields.filter(f => !f.removed && f.calculation && extensionAxis(f)?.axis === "area");
  if (calculated.length !== 1) return fields;
  const duplicates = fields.filter(f => !f.removed && !f.edited && !f.custom && f.original &&
    f.unit === "m²" && f.id !== calculated[0].id && f.subjectKind !== "extension" &&
    /\bextension\b.*\bfootprint\b/i.test(f.original.label));
  if (duplicates.length !== 1) return fields;
  const originalLocation = duplicates[0].original!.label.match(/\b(rear|front|side|left|right)\b/i)?.[1];
  const calculatedLocation = calculated[0].label.match(/\b(rear|front|side|left|right)\b/i)?.[1];
  if (originalLocation && calculatedLocation &&
    originalLocation.toLowerCase() !== calculatedLocation.toLowerCase()) return fields;
  return fields.map(f => f.id === duplicates[0].id ? { ...f, removed: true } : f);
}

/** Add empty inputs, not inferred foundation geometry or replacement drawing areas. */
export function withExtensionReviewFields(fields: MeasurementReviewField[], isExtension: boolean) {
  if (!isExtension && !fields.some(f => extensionAxis(f))) return fields;
  const next = [...fields];
  const groups = new Map<string, MeasurementReviewField>();
  for (const field of fields) {
    const axis = extensionAxis(field);
    if (axis && axis.axis !== "area") groups.set(axis.scope, field);
  }
  for (const [scope, field] of groups) {
    if (fields.some(f => {
      const axis = extensionAxis(f);
      return axis?.axis === "area" && belongsToScope(axis, extensionAxis(field)!,
        [...groups.values()].map(f => extensionAxis(f)!));
    })) continue;
    const id = `review-footprint-${normalise(scope).replace(/ /g, "-")}`;
    if (fields.some(f => f.id === id)) continue; // A deliberately removed field stays removed.
    next.push({ id, label: `${extensionAxis(field)!.label} footprint area`, unit: "m²", value: "", note: "",
      subjectId: field.subjectId, subjectLabel: field.subjectLabel, subjectKind: field.subjectKind });
  }
  if (!fields.some(isFootingLength) && !fields.some(f => f.id === "review-footing-length")) {
    next.push({ id: "review-footing-length", label: "Footing length", unit: "m", value: "",
      note: "Total length of the footings to be built, in linear metres. Enter the actual foundation runs; do not assume the full footprint perimeter." });
  }
  return next;
}

/** Recalculate only the edited extension's rectangular footprint, never its footing run. */
export function updateReviewMeasurement(
  fields: MeasurementReviewField[], id: string, patch: Partial<MeasurementReviewField>,
  initialise = false,
) {
  const next = withoutDuplicateCalculatedFootprint(fields).map(f =>
    f.id === id && !initialise ? { ...f, ...patch, edited: true, calculation: undefined } : f);
  const edited = next.find(f => f.id === id);
  if (!edited) return next;
  const changed = extensionAxis(edited) ||
    (fields.some(f => f.id === id) ? extensionAxis(fields.find(f => f.id === id)!) : null);
  if (!changed || changed.axis === "area") return next;
  const groups = new Map<string, ExtensionAxis>([[changed.scope, changed]]);
  for (const field of next) {
    const axis = extensionAxis(field);
    if (axis && axis.axis !== "area") groups.set(axis.scope, axis);
  }
  const scopes = [...groups.values()];
  const matching = next.filter(field => {
    const axis = extensionAxis(field);
    return axis && belongsToScope(axis, changed, scopes);
  });
  const areas = matching.filter(f => extensionAxis(f)?.axis === "area");
  // Duplicate spans/areas can represent different geometry. Do not choose one arbitrarily.
  if (areas.length !== 1) return next;
  const lengths = matching.filter(f => extensionAxis(f)?.axis === "length");
  const widths = matching.filter(f => extensionAxis(f)?.axis === "width");
  const length = lengths.length === 1 && lengths[0].value.trim() ? Number(lengths[0].value) : NaN;
  const width = widths.length === 1 && widths[0].value.trim() ? Number(widths[0].value) : NaN;
  const product = length * width;
  const valid = Number.isFinite(length) && length > 0 && Number.isFinite(width) && width > 0 &&
    Number.isFinite(product) && product > 0;
  const area = areas[0];
  // Opening/restoring a review must not overwrite an independently entered area.
  // Changing an axis explicitly still recalculates it as before.
  if (initialise && area.edited && !area.calculation) return next;
  if (!valid) {
    // Only clear a stale calculation; never discard an independent drawing/manual area.
    return area.calculation ? next.map(f => f.id === area.id
      ? { ...f, value: "", calculation: undefined, edited: true } : f) : next;
  }
  const value = String(Number(product.toPrecision(12)));
  const calculation = `Rectangular footprint: ${length} m × ${width} m = ${value} m². ` +
    "Calculated from reviewed overall dimensions, not a surveyed area or footing length. Edit the area for an irregular layout.";
  return withoutDuplicateCalculatedFootprint(next.map(f => f.id === area.id ? { ...f, value, calculation, edited: true } : f));
}

/** AI-read axes are available before the first keystroke. Calculate at review
 * initialisation too, without relabelling those axes as builder edits. */
export function initialiseExtensionFootprints(fields: MeasurementReviewField[]) {
  const scopes = new Map<string, string>();
  for (const field of fields) {
    const axis = extensionAxis(field);
    if (axis && axis.axis !== "area") scopes.set(axis.scope, field.id);
  }
  let next = fields;
  for (const id of scopes.values()) next = updateReviewMeasurement(next, id, {}, true);
  return withoutDuplicateCalculatedFootprint(next);
}
